import { useCallback, useContext, useMemo } from 'react';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  useSetNewGroupId,
  useSetOpenAutoPricingForGroupId,
} from 'src/contexts/NewGroupScrollContext/NewGroupScroll.atoms';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddToGroupListingsModal } from 'src/modals/GroupListings';
import {
  flattenListingGroup,
  sortListingsBySortId,
} from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { newGuidId } from 'src/utils/idUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  EventWithData,
  Feature,
  Listing,
  ListingGroupClient,
  MergeListingGroupInput,
} from 'src/WebApiController';

export const useBulkQuickGroup = (eventData: EventWithData) => {
  const { setModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const hasListingGroupAccountDefaultsFeature = useUserHasFeature(
    Feature.ListingGroupAccountDefaults
  );
  const hasSelectGroupAfterCreationFeature = useUserHasFeature(
    Feature.SelectGroupAfterCreation
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasQuickGroupBypassPreviewFeature = useUserHasFeature(
    Feature.QuickGroupBypassPreview
  );

  const { data: accountDefaults } = useListingGroupSellerAccountSettings();

  const setNewGroupId = useSetNewGroupId();
  const setOpenAutoPricingForGroupId = useSetOpenAutoPricingForGroupId();

  const { filterQueryWithEventIds, isAllListingsSelected } =
    useInventoryEventBulkActionsState(eventData.event);

  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { setActivePosEntity } = useActivePosEntityContext();
  const { setSelectionMode } = useMultiSelectionContext();

  const eventListings = useMemo(() => {
    return (
      (eventData?.entities?.listings?.flatMap(
        flattenListingGroup
      ) as Listing[]) ?? []
    );
  }, [eventData?.entities?.listings]);

  const selectedListingIds = useMemo(() => {
    if (isAllListingsSelected) {
      return filterQueryWithEventIds.entityIds?.length
        ? filterQueryWithEventIds.entityIds
        : eventListings.map((listing) => listing.id);
    }
    return filterQueryWithEventIds.entityIds ?? [];
  }, [eventListings, filterQueryWithEventIds.entityIds, isAllListingsSelected]);

  const onGroupCreatedCallback = useCallback(
    (groupId: string) => {
      if (hasSelectGroupAfterCreationFeature) {
        setNewGroupId(groupId);
        if (hasAutoPricingFeature) {
          setOpenAutoPricingForGroupId(groupId);
        }
      }
    },
    [
      hasAutoPricingFeature,
      hasSelectGroupAfterCreationFeature,
      setNewGroupId,
      setOpenAutoPricingForGroupId,
    ]
  );

  const onQuickGroup = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (selectedListingIds.length === 0) return;

      const firstListingId = selectedListingIds[0];
      const firstListing = eventListings.find((l) => l.id === firstListingId);

      const groupName = firstListing
        ? `${[firstListing.seating.section, firstListing.seating.row].filter(Boolean).join(' ')} x${firstListing.unsoldQty}`.trim()
        : '';

      const newGroupId = newGuidId();

      const accountDesiredActiveListings =
        accountDefaults?.desiredActiveListings != null
          ? accountDefaults.desiredActiveListings
          : undefined;

      const accountPricingSettingsJson =
        accountDefaults?.pricingSettingsJson ?? undefined;

      const accountDeprioritizedQuantities =
        accountDefaults?.deprioritizedQuantities ?? undefined;

      const accountDefaultSortId = accountDefaults?.defaultSortId ?? undefined;

      if (hasQuickGroupBypassPreviewFeature) {
        // Bypass the modal and directly create the group using account defaults
        const selectedEventListings = eventListings.filter((l) =>
          selectedListingIds.includes(l.id)
        );

        const sortedListings =
          accountDefaultSortId != null
            ? sortListingsBySortId(selectedEventListings, accountDefaultSortId)
            : selectedEventListings;

        const accountPricingSettings = accountPricingSettingsJson
          ? (JSON.parse(accountPricingSettingsJson) as {
              undAbsAmt: number | null;
              undRelAmt: number | null;
            })
          : null;

        const accountDeprioritizedQtys = accountDeprioritizedQuantities
          ? accountDeprioritizedQuantities.split(',').filter(Boolean)
          : [];

        const groupInput: MergeListingGroupInput = {
          listingGroupId: newGroupId,
          name: groupName,
          listingGroupItems: sortedListings.map((l, idx) => ({
            listingId: l.id,
            priority: idx + 1,
            groupId: l.ltGrpId,
            undercutSetting: l.ltGrpUndercut,
          })),
          viagogoEventId: eventData.event.viagId,
          viagogoMappingId: eventData.event.mpId,
          desiredActiveListings: accountDesiredActiveListings ?? 1,
          minActiveListings: null,
          maxActiveListings: null,
          targetPercentage: null,
          deprioritizedQuantities: accountDeprioritizedQtys,
          marketplaceSettings: null,
          inheritListingGroupSettings: true,
          maintainPriceStagger: false,
          groupUndercutSetting: {
            undAbsAmt: accountPricingSettings?.undAbsAmt ?? null,
            undRelAmt: accountPricingSettings?.undRelAmt ?? null,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          },
          groupingMethods: null,
        };

        tryInvokeApi(
          async () => {
            const succeeded = await new ListingGroupClient(
              activeAccountWebClientConfig
            ).createListingGroup({
              ...groupInput,
              listingGroupItems: groupInput.listingGroupItems
                .filter((x) => x.listingId > 0)
                .map((item, index) => ({ ...item, priority: index + 1 })),
            });

            if (succeeded) {
              onGroupCreatedCallback(newGroupId);
              await refreshExpandedListItems();
              setActivePosEntity(0);
              setSelectionMode();
            }
          },
          (error) => {
            showErrorDialog('ListingGroupClient.createListingGroup', error, {
              trackErrorData: groupInput,
            });
          }
        );
      } else {
        setModal({
          children: (
            <AddToGroupListingsModal
              selectedListingIds={selectedListingIds}
              listingGroupId={newGroupId}
              viagVirtId={eventData.event.virtId}
              newListingGroupName={groupName}
              inheritListingGroupSettings={true}
              defaultDesiredActiveListings={accountDesiredActiveListings}
              defaultPricingSettingsJson={accountPricingSettingsJson}
              defaultDeprioritizedQuantities={accountDeprioritizedQuantities}
              defaultSortId={accountDefaultSortId}
              onGroupCreated={onGroupCreatedCallback}
            />
          ),
          clickOutsideToClose: true,
          size: 'slide-in',
        });
      }
    },
    [
      accountDefaults,
      activeAccountWebClientConfig,
      eventData.event.viagId,
      eventData.event.mpId,
      eventData.event.virtId,
      eventListings,
      hasQuickGroupBypassPreviewFeature,
      onGroupCreatedCallback,
      refreshExpandedListItems,
      selectedListingIds,
      setActivePosEntity,
      setModal,
      setSelectionMode,
      showErrorDialog,
    ]
  );

  const actionItems = useMemo(() => {
    if (!hasListingGroupAccountDefaultsFeature) {
      return [];
    }
    return [{ title: ContentId.QuickGroup, onClick: onQuickGroup }];
  }, [hasListingGroupAccountDefaultsFeature, onQuickGroup]);

  if (!hasListingGroupAccountDefaultsFeature) {
    return { dropdown: null, actionItems: [] };
  }

  return {
    dropdown: (
      <PosDropdownItem key="QuickGroup" onClick={onQuickGroup}>
        <Stack alignItems="center" gap="m" width="full">
          <Content id={ContentId.QuickGroup} />
        </Stack>
      </PosDropdownItem>
    ),
    actionItems,
  };
};
